/**
 * Product Mechanics Screen (Step 2)
 * Typology: Demand Pattern, Supply Model, Delivery Complexity.
 */
import React, { useState, useEffect } from 'react';
import { Card, Button, Space, Typography, Tooltip } from 'antd';
import {
  getProductDomainConfig,
  getProductDomainPhaseData,
  upsertProductDomainPhaseData,
  updateProductDomainSession,
} from '../../services/productDomainSessionApi';
import type { ProductDomainConfig } from '../../types/productDomain';

const { Text } = Typography;

interface ProductMechanicsScreenProps {
  sessionId: string;
  onNext: () => void;
}

export const ProductMechanicsScreen: React.FC<ProductMechanicsScreenProps> = ({
  sessionId,
  onNext,
}) => {
  const [config, setConfig] = useState<ProductDomainConfig | null>(null);
  const [demandPattern, setDemandPattern] = useState<string | null>(null);
  const [supplyModel, setSupplyModel] = useState<string | null>(null);
  const [deliveryComplexity, setDeliveryComplexity] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const typologyPhase = config?.phase_definitions?.find(
    (p: { phase_id: string }) => p.phase_id === 'typology'
  );

  useEffect(() => {
    const load = async () => {
      const [cfg, phaseData] = await Promise.all([
        getProductDomainConfig(),
        getProductDomainPhaseData(sessionId, 'typology'),
      ]);
      setConfig(cfg);
      if (phaseData?.phase_data?.typology) {
        for (const item of phaseData.phase_data.typology) {
          if (item.question_id === 'demand_pattern') setDemandPattern(item.value_text ?? null);
          if (item.question_id === 'supply_model') setSupplyModel(item.value_text ?? null);
          if (item.question_id === 'delivery_complexity') setDeliveryComplexity(item.value_text ?? null);
        }
      }
      setLoading(false);
    };
    load();
  }, [sessionId]);

  const demandOptions = (typologyPhase?.questions?.find((q: { id: string; options?: string[] }) => q.id === 'demand_pattern')?.options ?? []) as string[];
  const supplyOptions = (typologyPhase?.questions?.find((q: { id: string; options?: string[] }) => q.id === 'supply_model')?.options ?? []) as string[];
  const deliveryOptions = (typologyPhase?.questions?.find((q: { id: string; options?: string[] }) => q.id === 'delivery_complexity')?.options ?? []) as string[];

  const handleSaveAndNext = async () => {
    if (!demandPattern || !supplyModel || !deliveryComplexity) return;
    setSaving(true);
    const [phaseOk] = await Promise.all([
      upsertProductDomainPhaseData(sessionId, 'typology', [
        { question_id: 'demand_pattern', value_type: 'select', value_text: demandPattern },
        { question_id: 'supply_model', value_type: 'select', value_text: supplyModel },
        { question_id: 'delivery_complexity', value_type: 'select', value_text: deliveryComplexity },
      ]),
      updateProductDomainSession(sessionId, {
        demand_pattern: demandPattern,
        supply_model: supplyModel,
        delivery_complexity: deliveryComplexity,
      }),
    ]);
    setSaving(false);
    if (phaseOk) onNext();
  };

  const formatLabel = (v: string) =>
    v.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  const DEMAND_HOVER: Record<string, string> = {
    continuous:
      'Customers engage on an ongoing basis. Growth depends primarily on retention, expansion, and lifetime value rather than one-time acquisition.',
    cyclical:
      'Purchases follow a predictable schedule (budget cycle, season, renewal period). Growth depends on timing alignment and renewal capture.',
    event_driven:
      'Purchases occur when a specific condition or trigger arises (failure, compliance, initiative). Growth depends on identifying and responding to triggers quickly.',
  };

  const SUPPLY_HOVER: Record<string, string> = {
    scalable:
      'Additional units can be delivered with low incremental effort. Growth is primarily constrained by demand, not production capacity.',
    capacity_bound:
      'Growth requires proportional increases in labor, facilities, or physical production. Throughput limits scale.',
    network_dependent:
      'Value depends on participation from multiple external actors. Growth requires ecosystem balance, not just internal execution.',
  };

  const DELIVERY_HOVER: Record<string, string> = {
    instant:
      'Customers receive value immediately upon access or purchase. Minimal setup or coordination required.',
    configuration_required:
      'Setup, onboarding, or integration is required before value is realized. Time-to-value depends on execution quality.',
    multi_actor:
      'Multiple stakeholders must align or adopt for value to emerge. Delivery depends on coordinated adoption.',
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      <Typography.Title level={4}>How does this product create and deliver value?</Typography.Title>

      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Card title="Demand Pattern">
          <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
            Demand Pattern defines how and when customers decide to purchase. This determines how growth behaves —
            whether it depends on retention, timing cycles, or triggering events.
          </Typography.Paragraph>
          <Space wrap>
            {demandOptions.map((opt: string) => (
              <Tooltip key={opt} title={DEMAND_HOVER[opt] ?? ''}>
                <Button
                  type={demandPattern === opt ? 'primary' : 'default'}
                  onClick={() => setDemandPattern(opt)}
                >
                  {formatLabel(opt)}
                </Button>
              </Tooltip>
            ))}
          </Space>
        </Card>
        <Card title="Supply Model">
          <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
            Supply Model defines how the product is produced or provisioned. This determines what physically limits
            growth — marginal cost, capacity, or ecosystem participation.
          </Typography.Paragraph>
          <Space wrap>
            {supplyOptions.map((opt: string) => (
              <Tooltip key={opt} title={SUPPLY_HOVER[opt] ?? ''}>
                <Button
                  type={supplyModel === opt ? 'primary' : 'default'}
                  onClick={() => setSupplyModel(opt)}
                >
                  {formatLabel(opt)}
                </Button>
              </Tooltip>
            ))}
          </Space>
        </Card>
        <Card title="Delivery Complexity">
          <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
            Delivery Complexity defines how value is realized after purchase. This determines how quickly customers
            experience value and where adoption friction may occur.
          </Typography.Paragraph>
          <Space wrap>
            {deliveryOptions.map((opt: string) => (
              <Tooltip key={opt} title={DELIVERY_HOVER[opt] ?? ''}>
                <Button
                  type={deliveryComplexity === opt ? 'primary' : 'default'}
                  onClick={() => setDeliveryComplexity(opt)}
                >
                  {formatLabel(opt)}
                </Button>
              </Tooltip>
            ))}
          </Space>
        </Card>

        <Button
          type="primary"
          onClick={handleSaveAndNext}
          loading={saving}
          disabled={!demandPattern || !supplyModel || !deliveryComplexity}
        >
          Save & Next
        </Button>
      </Space>
    </div>
  );
};
